import React, { useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ContactInfo: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const safeFullName = fullName.trim() || 'N/A';
    const safeEmail = email.trim() || 'N/A';
    const safePhone = phone.trim() || 'N/A';
    const safeSubject = subject.trim() || 'Medical supplies enquiry';
    const safeMessage = message.trim() || 'N/A';

    const mailBody = [
      `Name: ${safeFullName}`,
      `Email: ${safeEmail}`,
      `Phone: ${safePhone}`,
      `Subject: ${safeSubject}`,
      ``,
      safeMessage,
    ].join('\n');

    // Open the user's mail client with a pre-filled email.
    const mailto = `mailto:sales@manlab.co.zw?subject=${encodeURIComponent(
      safeSubject
    )}&body=${encodeURIComponent(mailBody)}`;

    window.location.href = mailto;
    setSent(true);
  };

  return (
<section className="py-12 sm:py-20 bg-light">
    <div className="container-main">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-16">
          {[
            { icon: <LocationOnIcon fontSize="large" />, title: 'Harare Office', lines: ['130041 New Stands, Chizhanje', 'Mabvuku, Harare, Zimbabwe'] },
            { icon: <PhoneIcon fontSize="large" />, title: 'Phone (Harare)', lines: ['(+263) 77 413 4805'] },
            { icon: <EmailIcon fontSize="large" />, title: 'Email', lines: ['sales@manlab.co.zw', 'tendai@manlab.co.zw'] },
          ].map(c => (
            <div key={c.title} className="card p-6 sm:p-8 text-center hover:-translate-y-1 transition-transform duration-300 border border-gray-100 shadow-sm bg-white">
              <div className="w-16 h-16 bg-light rounded-2xl flex items-center justify-center mx-auto mb-5 text-primary">
                {c.icon}
              </div>
              <h3 className="font-bold text-primary mb-3 text-base">{c.title}</h3>
              {c.lines.map((l, i) => <p key={i} className="text-gray-500 text-sm break-words">{l}</p>)}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-1 gap-10">
          <div className="card p-10 border border-gray-100 shadow-xl bg-white">
            <h2 className="text-2xl font-bold text-primary mb-2">Let's Talk About Your Project</h2>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              If you've got questions about our products, pricing, or distribution, send us a message. We'll get back to you within 24 hours.
            </p>

            {sent ? (
              <div className="text-center py-16 bg-light rounded-2xl border border-gray-100">
                <CheckCircleIcon className="text-primary mb-4" sx={{ fontSize: 64 }} />
                <h3 className="font-extrabold text-primary text-xl mb-2">Message Sent Successfully!</h3>
                <p className="text-gray-500 text-sm">Thank you for reaching out. We'll be in touch within 24 hours.</p>
                <button onClick={() => setSent(false)} className="mt-8 text-sm font-semibold text-primary underline">
                  Send another message
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input
                    type="text"
                    required
                    placeholder="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-5 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent bg-gray-50/50"
                  />
                  <input
                    type="email"
                    required
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-5 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent bg-gray-50/50"
                  />
                </div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent bg-gray-50/50"
                />
                <input
                  type="text"
                  placeholder="Subject / Products Required"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent bg-gray-50/50"
                />
                <textarea
                  rows={5}
                  required
                  placeholder="Your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent bg-gray-50/50 resize-none"
                />
                <div className="flex flex-col sm:flex-row gap-4 mt-2">
                  <button type="submit" className="btn-primary flex-1 justify-center py-4 text-base shadow-lg shadow-accent/20">
                    Send Message <SendIcon fontSize="small" className="ml-2" />
                  </button>
                  <a 
                    href="https://wa.me/263774134805?text=Hi, I would like to make an enquiry."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 py-4 px-6 rounded-xl bg-[#25D366] text-white font-bold text-base shadow-lg hover:opacity-90 transition-all"
                  >
                    Send via WhatsApp
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;
